// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React, { FC } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import SelectInline, { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useMemo } from "react";

const iconSize = pxToRem(18);
const iconOffset = pxToRem(0);
/* Starter Custom Code - End */

// Foundation --->
import { languageCodeWithoutLocal } from "@store/Framework/Common/Utilities/formatLanguages";
import GlobeFill from "@store/Library/Components/Icons/Globe/GlobeFill";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
// <--- Foundation

const enum fields {
    showIcon = "showIcon",
    // Foundation
    displayName = "displayName",
    displayMenuOnTop = "displayMenuOnTop",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    // Starter Custom Code
    selectInline?: SelectInlineStyles;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            justify-content: center;
                        `,
                        null,
                        css`
                            justify-content: flex-end;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    languageIcon: {
        size: 22,
        src: GlobeFill,
        css: css`
            margin-right: ${pxToRem(5)};
        `,
    },
    languageSelect: {},
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
        // Foundation
        [fields.displayName]: boolean;
        [fields.displayMenuOnTop]: boolean;
    };
}

/* Starter Custom Code - Start */
export const LogoImage = styled.img`
    width: ${iconSize};
    height: ${iconSize};
    background: currentColor;
    position: absolute;
    border-radius: 50%;
    object-fit: cover;
    overflow: hidden;
`;
/* Starter Custom Code - End */

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    /* Foundation ---> */
    const displayedLanguage = languages.find(x => x.id !== currentLanguage?.id);
    const displayedLanguageText = fields.displayName ? displayedLanguage?.description : displayedLanguage?.languageCode;

    const currentLanguageStyle: LinkPresentationProps = {
        typographyProps: {
            color: "text.main",
            css: css`
                display: flex;
                align-items: center;
                font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
                ${fields.displayName ? null : "text-transform: uppercase;"}
            `,
        },
        css: css`
            height: 100%;

            &:hover {
                text-decoration: underline;
            }
        `,
    };

    const changeLanguage = () => {
        const languageId = displayedLanguage?.id ?? "";
        setLanguage({ languageId });
    };
    /* <--- Foundation */

    return (
        <StyledWrapper {...styles.languageWrapper}>
            {/* Foundation ---> */}
            <Link {...currentLanguageStyle} onClick={changeLanguage}>
                {fields.showIcon &&
                    (currentLanguage?.imageFilePath ? (
                        <LogoImage
                            src={currentLanguage.imageFilePath}
                            alt={currentLanguage.languageCode} // Starter Custom Code
                            aria-hidden="true" // Starter Custom Code
                        />
                    ) : (
                        <Icon {...styles.languageIcon} />
                    ))}
                {displayedLanguage ? languageCodeWithoutLocal(displayedLanguageText) : "--"}
                {/* A11Y - Hidden Language Accessibility */}
                <VisuallyHidden lang={displayedLanguage?.languageCode}>
                    {languageCodeWithoutLocal(displayedLanguage?.description)}
                </VisuallyHidden>
            </Link>
            {/* <--- Foundation */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            // Foundation --->
            {
                name: fields.displayName,
                displayName: "Display Name Instead of Language Code",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            // <--- Foundation
        ],
        group: "Common",
    },
};

export default widgetModule;
